import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import { mensajeErrorDescarga } from "../../../utils/descargas";
import { Tabs, message } from "antd";
import ListaTraslados from "./ListaTraslados";
import AutorizacionesTabs from "./AutorizacionesTabs";
import "./Traslados.css"; // Archivo CSS para estilos personalizados

const CenterControl = React.memo(() => {
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [regimenes, setRegimenes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportDateRange, setExportDateRange] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Filtros de búsqueda
  const [selectedZona, setSelectedZona] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [selectedCopago, setSelectedCopago] = useState([
    "con_copago",
    "sin_copago",
  ]);

  // Optimizar carga inicial - solo cargar autorizaciones cuando sea necesario
  const fetchAutorizaciones = useCallback(async (includeRelations = false) => {
    try {
      const response = await api.get("/autorizaciones", {
        params: {
          limit: 999999, // Sin límite
          includeRelations: includeRelations // Carga condicional de relaciones
        }
      });

      // Validar que la respuesta sea un array
      let autorizacionesData = [];
      if (Array.isArray(response.data)) {
        autorizacionesData = response.data;
      } else if (response.data && Array.isArray(response.data.rows)) {
        // Si la respuesta tiene estructura { rows: [...] }
        autorizacionesData = response.data.rows;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Si la respuesta tiene estructura { data: [...] }
        autorizacionesData = response.data.data;
      } else {
        console.warn(
          "⚠️ Respuesta de autorizaciones no es un array:",
          response.data
        );
        autorizacionesData = [];
      }

      setAutorizaciones(autorizacionesData);
    } catch (error) {
      console.error("Error fetching autorizaciones:", error);
      setAutorizaciones([]); // Asegurar que siempre sea un array
    }
  }, []);

  const fetchRegimenes = useCallback(async () => {
    try {
      const response = await api.get("/regimenes");
      setRegimenes(response.data);
    } catch (error) {
      console.error("Error fetching regimenes:", error);
    }
  }, []);

  useEffect(() => {
    fetchAutorizaciones();
    fetchRegimenes();
  }, [fetchAutorizaciones, fetchRegimenes]);

  // Memoizar handlers para evitar re-renders
  const handleOpenDialog = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleProgramarTraslado = useCallback(() => {
    // Lógica para programar un traslado
  }, []);

  // Handlers de paginación optimizados
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((filters = {}) => {
    setSelectedZona(filters.selectedZona || "");
    setSelectedStatuses(filters.selectedStatuses || []);
    setStartDate(filters.startDate ? new Date(filters.startDate) : null);
    setEndDate(filters.endDate ? new Date(filters.endDate) : null);
    setStartTime(filters.startTime ? new Date(filters.startTime) : null);
    setEndTime(filters.endTime ? new Date(filters.endTime) : null);
    setSelectedCopago(filters.selectedCopago || ["con_copago", "sin_copago"]);
  }, []);

  const handleCopagoFilter = useCallback((values = []) => {
    setSelectedCopago(
      values.length > 0 ? values : ["con_copago", "sin_copago"]
    );
  }, []);

  const handleExportTraslados = useCallback(async () => {
    if (!exportDateRange?.[0] || !exportDateRange?.[1]) {
      message.error("Seleccione un rango de fechas");
      return;
    }

    try {
      setExportLoading(true);
      const [start, end] = exportDateRange;

      const response = await api.get("/traslados/exportar", {
        params: {
          fechaInicio: start.format("YYYY-MM-DD"),
          fechaFin: end.format("YYYY-MM-DD"),
        },
        responseType: "blob",
      });

      // Crear descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Traslados_${start.format("YYYYMMDD")}_${end.format(
        "YYYYMMDD"
      )}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);

      message.success("Traslados exportados");
      setExportModalVisible(false);
    } catch (error) {
      message.error(await mensajeErrorDescarga(error, "Error al exportar"));
    } finally {
      setExportLoading(false);
    }
  }, [exportDateRange]);

  // Memoizar props complejos para evitar re-renders de componentes hijos
  const listasTrasladosProps = useMemo(() => ({
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleProgramarTraslado,
    page,
    rowsPerPage,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handleRowsPerPageChange,
    selectedZona,
    setSelectedZona,
    selectedStatuses,
    setSelectedStatuses,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    handleFilterChange,
    selectedCopago,
    onCopagoFilter: handleCopagoFilter,
    exportModalVisible,
    setExportModalVisible,
    exportLoading,
    exportDateRange,
    setExportDateRange,
    handleExportTraslados,
    onExport: () => setExportModalVisible(true),
    regimenes
  }), [
    openDialog, handleOpenDialog, handleCloseDialog, handleProgramarTraslado,
    page, rowsPerPage, handlePageChange, handleRowsPerPageChange,
    selectedZona, selectedStatuses, startDate, endDate, startTime, endTime,
    handleFilterChange, selectedCopago, handleCopagoFilter,
    exportModalVisible, exportLoading, exportDateRange,
    handleExportTraslados, regimenes
  ]);

  // Memoizar configuración de tabs
  const tabsItems = useMemo(() => [
    {
      key: "1",
      label: "Lista de Traslados",
      children: <ListaTraslados {...listasTrasladosProps} />,
    },
    {
      key: "2", 
      label: "Autorizaciones",
      children: <AutorizacionesTabs autorizaciones={autorizaciones} />,
    },
  ], [listasTrasladosProps, autorizaciones]);

  return (
    <div className="CenterControl-content">
      <div className="sticky-header">
        <Tabs
          defaultActiveKey="1"
          centered
          items={tabsItems}
        />
      </div>
    </div>
  );
});

export default CenterControl;
